import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Chip, StudioButton } from "@/features/studio/theme";
import { titleCase } from "@/lib/format";
import { FieldGroupLabel } from "./field-label";

/**
 * Pick cities from a list, with "Select all" and a box to add one that is not
 * listed. Cities the user adds, and selected cities missing from `cities`
 * (say, saved earlier and since removed from the list), stay as chips.
 */
export function CityChipsPicker({
	label,
	cities,
	selected,
	onChange,
	addPlaceholder,
	hint,
	exclude,
}: {
	label: string;
	cities: readonly string[];
	selected: readonly string[];
	onChange: (next: string[]) => void;
	addPlaceholder: string;
	hint?: string;
	/** A city not offered here, e.g. the realtor's primary market. */
	exclude?: string;
}) {
	const { t } = useTranslation();
	const [extraCities, setExtraCities] = useState<string[]>([]);
	const [customCity, setCustomCity] = useState("");

	// Keep selected cities that are not in the list visible as chips.
	useEffect(() => {
		const missing = selected.filter(
			(city) => !cities.includes(city) && !extraCities.includes(city),
		);
		if (missing.length > 0) {
			setExtraCities((prev) => [...prev, ...missing]);
		}
	}, [selected, cities, extraCities]);

	const options = [
		...cities,
		...extraCities.filter((city) => !cities.includes(city)),
	].filter((city) => city !== exclude);

	const toggle = (city: string) => {
		onChange(
			selected.includes(city)
				? selected.filter((item) => item !== city)
				: [...selected, city],
		);
	};

	const allSelected =
		options.length > 0 && options.every((city) => selected.includes(city));

	const toggleAll = () => {
		onChange(
			allSelected
				? selected.filter((city) => !options.includes(city))
				: [...new Set([...selected, ...options])],
		);
	};

	const addCustom = () => {
		const normalized = titleCase(customCity);
		if (!normalized) return;
		const canonical =
			options.find((city) => city.toLowerCase() === normalized.toLowerCase()) ??
			normalized;
		if (!options.includes(canonical) && canonical !== exclude) {
			setExtraCities((prev) => [...prev, canonical]);
		}
		if (!selected.includes(canonical) && canonical !== exclude) {
			onChange([...selected, canonical]);
		}
		setCustomCity("");
	};

	return (
		<fieldset className="min-w-0">
			<FieldGroupLabel as="legend">{label}</FieldGroupLabel>
			<div className="mb-3 flex items-start justify-between gap-3">
				<p className="font-body text-[13px] text-dashboard-muted">{hint}</p>
				<button
					type="button"
					onClick={toggleAll}
					className="shrink-0 cursor-pointer border-none bg-transparent font-body text-xs text-dashboard-accent-dark"
				>
					{allSelected ? t("common.deselectAll") : t("common.selectAll")}
				</button>
			</div>
			<div className="mb-3 flex flex-wrap gap-2">
				{options.map((city) => (
					<Chip
						key={city}
						active={selected.includes(city)}
						onClick={() => toggle(city)}
					>
						{city}
					</Chip>
				))}
			</div>
			<div className="flex gap-2">
				<input
					type="text"
					value={customCity}
					aria-label={addPlaceholder}
					onChange={(event) => setCustomCity(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							addCustom();
						}
					}}
					placeholder={addPlaceholder}
					className="h-10 min-w-0 flex-1 rounded-[7px] border border-dashboard-border bg-dashboard-warm-450 px-3.5 font-body text-sm text-dashboard-text outline-none transition-all duration-200 focus:border-dashboard-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)]"
				/>
				<StudioButton
					type="button"
					variant="secondary"
					size="sm"
					onClick={addCustom}
					disabled={!customCity.trim()}
				>
					{t("onboarding.fields.addCustom")}
				</StudioButton>
			</div>
		</fieldset>
	);
}
